import React, { Fragment, } from 'react';
import { Button, } from 'reactstrap';
import { get, pick, omit, uniq, uniqBy, keyBy, } from 'lodash';
import { toast, } from 'react-toastify';
import { useCounter, useToggle, } from 'react-use';
import { Link } from 'react-router-dom';
import { format as formatDate } from 'date-fns';
import classnames from 'classnames';

import firebase from '../../firebase';
import { isMobile } from '../../util';
import { idWithTenant, } from '../../shared/util';
import { fieldDisplayValue, } from '../../shared/util';
import { statuses, } from '../../shared/models/inventory';
import { newId } from '../../util';
import useCollectionSubscription from '../hooks/useCollectionSubscription';
import useDocumentsSubscription from '../hooks/useDocumentsSubscription';
import useDocumentsFetch from '../hooks/useDocumentsFetch';
import useQueryParams from '../hooks/useQueryParams';
import AdminPage from '../hocs/AdminPage';
import ReceivementFormModal from '../modals/ReceivementFormModal';
import RevertingShelfModal from '../modals/RevertingShelfModal';
import BatchRevertingShelfModal from '../modals/BatchRevertingShelfModal';
import UnknownLabelModal from '../modals/UnknownLabelModal';
import UnknownShelvingModal from '../modals/UnknownShelvingModal';
import UnknownPickingModal from '../modals/UnknownPickingModal';
import QueryInput from '../QueryInput';
import QueryBoolean from '../QueryBoolean';
import AddButton from '../AddButton';
import EditButton from '../EditButton';
import DeleteButton from '../DeleteButton';
import TenantTags from '../TenantTags';

const db = firebase.firestore();
const tenantsRef = db.collection('tenants');
const tagsRef = db.collection('tags');
const unknownInventoriesRef = db.collection('unknownInventories');
const locationsRef = db.collection('locations');

export default AdminPage(function AdminReceivements (props) {
  const queryParams = useQueryParams();
  const {
    onlyResubmitted = '0',
  } = queryParams;
  const unknownInventories = useCollectionSubscription(unknownInventoriesRef.where('status', '==', 'shelved').orderBy('createdAt', 'desc'));
  const locations = useCollectionSubscription(locationsRef.orderBy('sortKey'));
  const locationsById = keyBy(locations, 'id');
  const receivingRequests = useDocumentsFetch(unknownInventories.map(_ => tenantsRef.doc(_.tenantId).collection('receivingRequests').doc(_.receivingRequestId)), [unknownInventories]);
  const receivingRequestsByFullId = keyBy(receivingRequests, _ => idWithTenant(_.ref));
  const tenants = useCollectionSubscription(tenantsRef);
  const tenantsById = keyBy(tenants, 'id');
  const tags = useCollectionSubscription(tagsRef.orderBy('createdAt'));
  const tagsById = keyBy(tags, 'id');
  const rows = unknownInventories.map((unknownInventory) => {
    const { tenantId, locationId, receivingRequestFullId, } = unknownInventory;
    const receivingRequest = receivingRequestsByFullId[receivingRequestFullId];
    const tenant = tenantsById[tenantId];
    const location = locationsById[locationId];
    return { ...unknownInventory, receivingRequest, tenant, location };
  });

  // NOTE: filter
  let filteredRows = rows;
  if(onlyResubmitted === '1') {
    filteredRows = filteredRows.filter(_ => _.receivingRequest?.status === 'resubmitted');
  }


  return (
    <div className="admin-inventories container-fluid">
      <div className={classnames('py-4 px-md-4 my-4 mb-5', { 'bg-white': !isMobile() })}>
        <div className="d-flex justify-content-center mb-3">
          <h4>不明在庫</h4>
        </div>
        <div className="mb-3 d-flex justify-content-start align-items-center gap-1">
          <QueryBoolean paramName="onlyResubmitted" label="再入庫待ちのみ" defaultValue={'0'} />
        </div>
        <div className="d-flex justify-content-start align-items-end mb-3 gap-1">
          <AddButton iconClassName="fas fa-sign-in-alt" label="不明ラベル発行" FormModal={UnknownLabelModal} formProps={{ tenants, locations, }} />
          <AddButton iconClassName="fas fa-sign-in-alt" label="不明入庫" FormModal={UnknownShelvingModal} formProps={{ tenants, locations, }} />
          <AddButton iconClassName="fas fa-sign-out-alt" label="不明出庫" FormModal={UnknownPickingModal} formProps={{ tenants, locations, }} />
        </div>
        <div className="mt-4 overflow-scroll position-relative" style={{ zIndex: 0 }}>
          {
            rows.length > 0 ? (
              isMobile() ? (
                <div className="d-flex flex-column gap-2">
                  {
                    filteredRows.map((unknownInventory) => {
                      const { id, ref, status, receivingRequest, tenant, location, locationId, createdAt, receivingRequestFullId, } = unknownInventory;
                      const { label: statusLabel, color } = statuses[status];

                      return (
                        <div className={classnames('card p-2 gap-1', `border-${color}`)}>
                          {receivingRequest?.status === 'resubmitted' && <span className="badge badge-info">再入庫待ち</span>}
                          <div className="d-flex justify-content-between small text-muted">
                            <div>
                              {id}
                            </div>
                            <div>
                              {formatDate(createdAt.toDate(), 'yyyy/MM/dd HH:mm')}
                            </div>
                          </div>
                          <div className="d-flex justify-content-between">
                            <div className="h4">
                              {receivingRequestFullId}
                            </div>
                            <div className="h4">
                              {location?.name}
                            </div>
                          </div>
                        </div>
                      );
                    })
                  }
                </div>
              ) : (
                <table className="table sticky-table table-bordered">
                  <thead className="thead-light text-center">
                    <tr className="text-nowrap">
                      <th style={{ minWidth: 150 }}>ID</th>
                      <th style={{ minWidth: 120 }}>状態</th>
                      <th style={{ minWidth: 120 }}>テナントID</th>
                      <th style={{ minWidth: 150 }}>テナント名</th>
                      <th style={{ minWidth: 120 }}>入荷依頼ID</th>
                      <th style={{ minWidth: 150 }}>ロケーション</th>
                      <th style={{ minWidth: 150 }}>入庫日時</th>
                      <th style={{ minWidth: 150 }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {
                      filteredRows.map((unknownInventory) => {
                        const { id, ref, status, receivingRequest, tenant, location, locationId, createdAt, receivingRequestFullId, } = unknownInventory;
                        const { label: statusLabel, color } = statuses[status];

                        return (
                          <tr key={id} className={classnames(`table-${color}`)}>
                            <td>
                              {id}
                            </td>
                            <td>
                              {statusLabel}
                            </td>
                            <td>
                              {tenant && tenant.id}
                            </td>
                            <td>
                              {tenant && tenant.name}
                              <TenantTags tagIds={tenant?.tagIds} tagsById={tagsById} />
                            </td>
                            <td>
                              {receivingRequestFullId}
                              {receivingRequest?.status === 'resubmitted' && <span className="badge badge-info">再入庫待ち</span>}
                            </td>
                            <td>
                              {location?.name}
                            </td>
                            <td>
                              {formatDate(createdAt.toDate(), 'yyyy/MM/dd HH:mm')}
                            </td>
                            <td className="text-nowrap">
                              <DeleteButton label="元の入荷依頼を削除" item={receivingRequest} itemRef={receivingRequest?.ref} disabled={receivingRequest == null} />
                            </td>
                          </tr>
                        );
                      })
                    }
                  </tbody>
                </table>
              )
            ) : (
              <div>
                未出庫の不明在庫はありません
              </div>
            )
          }
        </div>
      </div>
    </div>
  );
});
